import { z } from "zod";

/**
 * Email-safe style properties. This is deliberately a small, flat set — every
 * property here must have a reliable mapping onto MJML attributes / inline CSS
 * that survives major clients (including Outlook desktop).
 */
export const StylePropsSchema = z
    .object({
        paddingTop: z.number().int().min(0).max(200),
        paddingRight: z.number().int().min(0).max(200),
        paddingBottom: z.number().int().min(0).max(200),
        paddingLeft: z.number().int().min(0).max(200),
        backgroundColor: z.string().regex(/^(#[0-9a-fA-F]{3,8}|transparent)$/),
        color: z.string().regex(/^#[0-9a-fA-F]{3,8}$/),
        fontFamily: z.string().max(300),
        fontSize: z.number().int().min(8).max(96),
        lineHeight: z.number().min(0.8).max(3),
        fontWeight: z.enum(["normal", "bold", "300", "400", "500", "600", "700", "800"]),
        textAlign: z.enum(["left", "center", "right", "justify"]),
        align: z.enum(["left", "center", "right"]),
        /** Pixel width (images, buttons); percentages only via widthPercent. */
        width: z.number().int().min(8).max(1200),
        widthPercent: z.number().min(1).max(100),
        height: z.number().int().min(1).max(1200),
        borderRadius: z.number().int().min(0).max(60),
        borderWidth: z.number().int().min(0).max(20),
        borderStyle: z.enum(["solid", "dashed", "dotted"]),
        borderColor: z.string().regex(/^#[0-9a-fA-F]{3,8}$/),
        letterSpacing: z.number().min(-2).max(10),
        textTransform: z.enum(["none", "uppercase", "lowercase", "capitalize"]),
        /** Visibility toggles resolved at render time. */
        hideOnDesktop: z.boolean(),
        hideOnMobile: z.boolean(),
    })
    .partial()
    .strict();

export type StyleProps = z.infer<typeof StylePropsSchema>;

/** Per-block-type style overrides (document/template layer). */
export const StyleSheetSchema = z.record(z.string(), StylePropsSchema);
export type StyleSheet = z.infer<typeof StyleSheetSchema>;

/** Brand kit tokens a tenant can define; mapped onto block styles at render. */
export const BrandTokensSchema = z
    .object({
        colors: z
            .object({
                primary: z.string(),
                secondary: z.string(),
                text: z.string(),
                background: z.string(),
                surface: z.string(),
                link: z.string(),
            })
            .partial(),
        fontFamilies: z
            .object({
                heading: z.string(),
                body: z.string(),
            })
            .partial(),
        button: StylePropsSchema,
        heading: StylePropsSchema,
        paragraph: StylePropsSchema,
        link: z.object({ color: z.string(), underline: z.boolean() }).partial(),
        logoAssetId: z.string(),
        logoUrl: z.string(),
        contentWidth: z.number().int().min(320).max(900),
        footerText: z.string(),
        companyName: z.string(),
        companyAddress: z.string(),
        socialLinks: z.array(
            z.object({ network: z.string(), url: z.string(), label: z.string().optional() }),
        ),
        unsubscribeText: z.string(),
    })
    .partial()
    .strict();

export type BrandTokens = z.infer<typeof BrandTokensSchema>;

export function mergeStyles(...layers: (StyleProps | undefined)[]): StyleProps {
    const out: StyleProps = {};
    for (const layer of layers) {
        if (layer) Object.assign(out, layer);
    }
    return out;
}
